import { NavItem } from 'epubjs/types/navigation';
import Section, { SpineItem } from 'epubjs/types/section';
import { Track } from '../types';
import type { EnhancedPage } from '../types/navigation';

type PagesArray = {
  [key: string]: string[];
};

const buildPagesArray = (pages: EnhancedPage[]): PagesArray => {
  const pagesArray: PagesArray = {};
  pages.forEach((page) => {
    const parentHref = page.href.split('#')[0] as string;
    if (!pagesArray[parentHref]) {
      pagesArray[parentHref] = [];
    }

    return pagesArray[parentHref].push(page.href);
  });

  return pagesArray;
};

/**
 * Builds a map of items, that level is lowe than currently chosen heading level.
 * Map value will be the parent element, which level is equal or lower than currently chosen heading level.
 */
export const buildChildParentNavigationDependency = (
  nav: NavItem[],
  pages: EnhancedPage[],
  desireLevel: number,
): Map<string, string> => {
  const dependency = new Map<string, string>();
  const pagesArray: PagesArray = buildPagesArray(pages);

  /**
   * This function was added because epubJS navigation object missing pages info.
   * We are adding pages data to this list with correct headings as a parent href.
   * So to overcome issue, when user is currently on page track, and want to navigate through the headings,
   * than we can find correct heading to navigate through using page href.
   */
  const addPages = (item: NavItem): void => {
    const fileHref = item.href.split('#')[0] as string;
    if (pagesArray[fileHref]) {
      pagesArray[fileHref].forEach((href) => dependency.set(href, item.href));
    }
  };
  const iterate = (navItems: NavItem[], level: number): void => {
    navItems.forEach((item: NavItem) => {
      addPages(item);

      if (level > desireLevel && item.parent) {
        if (dependency.has(item.parent)) {
          /**
           * So the item level is lower than 0 level. We should use parent href from previous element.
           * So that all items, that level is lowe than currently chosen heading level got the same parent
           * (href of the element which we should navigate through) href.
           */
          const previousParentElementParentHref = dependency.get(item.parent);
          if (!previousParentElementParentHref) {
            throw new Error('Incorrect navigation structure.');
          }
          dependency.set(item.href, previousParentElementParentHref);
        } else {
          // Case when item is one level lower than currently chosen heading level. It's parent href should be used for navigation.
          dependency.set(item.href, item.parent);
        }
      }
      if (item.subitems?.length) {
        iterate(item.subitems, level + 1);
      }
    });
  };
  iterate(nav, 0);

  return dependency;
};

/**
 * Builds a map of items, that level is lowe than currently chosen heading level.
 * Map value will be the parent element, which level is equal or lower than currently chosen heading level.
 */
export const buildChildParentPageNavigationDependency = (
  nav: NavItem[],
  pages: EnhancedPage[],
  getSpineByIdentifier: (identifier: string | number) => Section | null,
): Map<string, string> => {
  const dependency = new Map<string, string>();
  const pagesArray = buildPagesArray(pages);

  const addPages = (item: NavItem): void => {
    // Clean the HREF (remove fragment) for spine lookup
    const [fileHref] = item.href.split('#');

    const findCurrentItemPage = (spine: SpineItem | null | undefined): string => {
      // if we hit the start of the book or null
      if (!spine || !spine.href) {
        return pages.length > 0 ? pages[0].href : item.href;
      }

      // Check if this specific spine file has page markers
      if (pagesArray[spine.href]) {
        return pagesArray[spine.href][pagesArray[spine.href].length - 1];
      }

      // only call prev() if it's a function (Epub.js SpineItem)
      const prevSection = typeof spine.prev === 'function' ? spine.prev() : null;
      return findCurrentItemPage(prevSection);
    };

    if (pagesArray[fileHref]) {
      // Page exists in the same file as the heading
      const index = pages.findIndex((page) => page.href.startsWith(fileHref));
      if (index !== -1) {
        // Use the previous page if we aren't at the very start
        dependency.set(item.href, pages[index === 0 ? 0 : index - 1].href);
      }
    } else {
      // Heading has no page in its file, look backwards in the spine
      const currentSpine = getSpineByIdentifier(fileHref); // Use cleaned href

      if (currentSpine) {
        const prevSection = typeof currentSpine.prev === 'function' ? currentSpine.prev() : null;
        dependency.set(item.href, findCurrentItemPage(prevSection));
      } else {
        // If spine lookup fails, default to first page
        dependency.set(item.href, pages.length > 0 ? pages[0].href : item.href);
      }
    }
  };

  const iterate = (navItems: NavItem[]): void => {
    navItems.forEach((item) => {
      addPages(item);
      if (item.subitems?.length) {
        iterate(item.subitems);
      }
    });
  };

  iterate(nav);
  return dependency;
};

export const findCorrectPrevTrack = (
  skipTime: number,
  track: Track,
  currentPlayerTime: number,
  nextTrackGetter: (track: Track) => Track,
): Track => {
  if (!track || typeof track?.audio?.clipBegin !== 'number') {
    throw new Error('Incorrect track data.');
  }

  const skipDelta = currentPlayerTime - (track.audio.clipBegin || 0) - Math.abs(skipTime);
  if (skipDelta < 0) {
    const previousTrack = nextTrackGetter(track);
    if (!previousTrack || typeof previousTrack?.audio?.clipEnd !== 'number') {
      throw new Error('Incorrect track data.');
    }

    return findCorrectPrevTrack(skipDelta, previousTrack, previousTrack.audio.clipEnd, nextTrackGetter);
  }
  const newTime = currentPlayerTime - Math.abs(skipTime);
  return { ...track, audio: { ...track.audio, clipBegin: newTime } };
};

export const findCorrectNextTrack = (
  skipTime: number,
  track: Track,
  currentPlayerTime: number,
  nextTrackGetter: (track: Track) => Track,
): Track => {
  if (!track || typeof track?.audio?.clipBegin !== 'number') {
    throw new Error('Incorrect track data.');
  }

  const skipDelta = (track.audio.clipEnd || 0) - currentPlayerTime - Math.abs(skipTime);
  if (skipDelta < 0) {
    const previousTrack = nextTrackGetter(track);
    if (!previousTrack || typeof previousTrack?.audio?.clipEnd !== 'number') {
      throw new Error('Incorrect track data.');
    }

    return findCorrectNextTrack(skipDelta, previousTrack, previousTrack.audio.clipBegin || 0, nextTrackGetter);
  }
  const newTime = currentPlayerTime + Math.abs(skipTime);
  return { ...track, audio: { ...track.audio, clipBegin: newTime } };
};
